 import React, { 
useEffect, useState } from "react";
import {
  getClients,
  getDocuments,
  uploadDocument,
  deleteDocument,
  getDocumentUrl,
} from "../api";
import { useLocation } from "react-router-dom";

export default function Documents() {
  const location = useLocation();
  const queryClientId = new URLSearchParams(location.search).get("client_id");

  const [clients, setClients] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [documentType, setDocumentType] = useState("General Document");
  const [uploading, setUploading] = useState(false);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  async function loadClients() {
    const data = await getClients();
    setClients(Array.isArray(data) ? data : []);
  }

  async function loadDocuments() {
    try {
      setLoadingDocuments(true);
      setError("");

      const data = await getDocuments();
      setDocuments(Array.isArray(data) ? data : []);
    } catch (err) {
      setDocuments([]);
      setError(err?.message || "Failed to load documents");
    } finally {
      setLoadingDocuments(false);
    }
  }

  useEffect(() => {
    loadClients();
    loadDocuments();
  }, []);

  async function handleUpload(e) {
    e.preventDefault();

    if (!selectedFile) {
      setError("Please choose a file first");
      return;
    }

    try {
      setUploading(true);
      setError("");
      setSuccessMessage("");

      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("client_id", selectedClientId || "");
      formData.append("document_type", documentType);

      await uploadDocument(formData);

      setSelectedFile(null);
      setDocumentType("General Document");
      setSuccessMessage("Document uploaded successfully");
      await loadDocuments();

      const fileInput = document.getElementById("document-upload-input");
      if (fileInput) fileInput.value = "";
    } catch (err) {
      setError(err?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }

  async function handleDelete(documentId) {
    const confirmed = window.confirm("Delete this document?");
    if (!confirmed) return;

    try {
      setError("");
      setSuccessMessage("");

      await deleteDocument(documentId);
      setSuccessMessage("Document deleted successfully");
      await loadDocuments();
    } catch (err) {
      setError(err?.message || "Delete failed");
    }
  }

  function getClientName(clientId) {
    const client = clients.find((item) => String(item.id) === String(clientId));
    return client?.full_name || client?.name || (clientId ? `Client ${clientId}` : "Not assigned");
  }

  const visibleDocuments = queryClientId
    ? documents.filter((doc) => String(doc.client_id) === String(queryClientId))
    : documents;
  return (
    <div style={{ padding: "28px", background: "#f3f6fb", minHeight: "100vh" }}>
      <div style={headerRowStyle}>
        <div>
          <div style={eyebrowStyle}>Integrity Suite CRM</div>
          <h1 style={pageTitleStyle}>Documents</h1>
        </div>

        <button onClick={loadDocuments} style={topButtonStyle}>
          {loadingDocuments ? "Refreshing Documents..." : "Refresh Documents"}
        </button>
      </div>

      {error ? <AlertBox color="#ff4d4f" text={error} /> : null}
      {successMessage ? <AlertBox color="#22c55e" text={successMessage} /> : null}

      <div style={mainGridStyle}>
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>Upload Document</div>
          <div style={mutedTextStyle}>
            Upload client documents, IDs, reports, bureau responses, and supporting 
files.
          </div>

          <form onSubmit={handleUpload}>
            <label style={labelStyle}>Client</label>
            <select
              value={selectedClientId}
              onChange={(e) => setSelectedClientId(e.target.value)}
              style={inputStyle}
            >
              <option value="">No client selected</option>
              {clients.map((client) => (
                <option key={client.id} value={String(client.id)}>
                  {(client.full_name || `Client ${client.id}`) + ` (ID: ${client.id})`}
                </option>
              ))}
            </select>

            <label style={labelStyle}>Document Type</label>
            <select
              value={documentType}
              onChange={(e) => setDocumentType(e.target.value)}
              style={inputStyle}
            >
              <option value="General Document">General Document</option>
              <option value="Driver License">Driver License</option>
              <option value="Proof of Address">Proof of Address</option>
              <option value="Social Security Card">Social Security Card</option>
              <option value="Credit Report">Credit Report</option>
              <option value="Bureau Response">Bureau Response</option>
              <option value="Dispute Evidence">Dispute Evidence</option>
            </select>

            <label style={labelStyle}>File</label>
            <input
              id="document-upload-input"
              type="file"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
              style={fileInputStyle}
            />

            <div style={selectedFileStyle}>
              {selectedFile ? `Selected: ${selectedFile.name}` : "No file selected"}
            </div>

            <button type="submit" style={primaryButtonStyle}>
              {uploading ? "Uploading..." : "Upload Document"}
            </button>
          </form>
        </div>

        <div style={panelStyle}>
          <div style={savedHeaderStyle}>
            <div>
              <div style={panelHeaderStyle}>Saved Documents</div>
              <div style={mutedTextStyle}>Review, open, and manage uploaded files.</div>
            </div>
          </div>

          <div style={statsGridStyle}>
            <MiniStatCard
              title="Total Documents"
              value={visibleDocuments.length}
              subtitle="All uploaded files"
            />
          </div>

          {loadingDocuments ? (
            <div style={emptyStateStyle}>Loading documents...</div>
          ) : visibleDocuments.length === 0 ? (
            <div style={emptyStateStyle}>No documents uploaded yet.</div>
          ) : (
            <div style={{ display: "grid", gap: "18px" }}>
              {visibleDocuments.map((doc) => (
                <div key={doc.id} style={documentCardStyle}>
                  <div style={cardTopStyle}>
                    <div>
                      <div style={documentTitleStyle}>
                        {doc.filename || `Document ${doc.id}`}
                      </div>

                      <div style={infoLineStyle}>{"ID: " + doc.id}</div>
                      <div style={infoLineStyle}>
                        {"Client: " + getClientName(doc.client_id)}
                      </div>
                      <div style={infoLineStyle}>
                        {"Type: " + (doc.document_type || "General Document")}
                      </div>
                      <div style={infoLineStyle}>
                        {"Uploaded: " + formatTimestamp(doc.created_at)}
                      </div>
                    </div>

                    <div style={actionRowStyle}>
                      <a
                        href={getDocumentUrl(doc.id)}
                        target="_blank"
                        rel="noreferrer"
                        style={openButtonStyle}
                      >
                        Open
                      </a>

                      <button
                        type="button"
                        onClick={() => handleDelete(doc.id)}
                        style={deleteButtonStyle}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AlertBox({ color, text }) {
  return (
    <div
      style={{
        border: `2px solid ${color}`,
        borderRadius: "18px",
        padding: "18px 20px",
        marginBottom: "24px",
        background: "white",
        color: "#111827",
        fontSize: "18px",
      }}
    >
      {text}
    </div>
  );
}

function MiniStatCard({ title, value, subtitle }) {
  return (
    <div style={miniStatStyle}>
      <div style={miniStatTitleStyle}>{title}</div>
      <div style={miniStatValueStyle}>{value}</div>
      <div style={miniStatSubtitleStyle}>{subtitle}</div>
    </div>
  );
}

function formatTimestamp(value) {
  if (!value) return "Not available";

  try {
    const date = new Date(Number(value) * 1000);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString();
  } catch (error) {
    return String(value);
  }
}

const headerRowStyle = {
  marginBottom: "28px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "20px",
  flexWrap: "wrap",
};

const eyebrowStyle = {
  fontSize: "18px",
  color: "#6b7280",
  marginBottom: "8px",
  fontWeight: "500",
};

const pageTitleStyle = {
  fontSize: "64px",
  fontWeight: "800",
  margin: 0,
  lineHeight: 1,
  color: "#0f172a",
};

const mainGridStyle = {
  display: "grid",
  gridTemplateColumns: "1fr 1.4fr",
  gap: "24px",
  alignItems: "start",
};

const panelStyle = {
  background: "white",
  borderRadius: "24px",
  padding: "24px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
};

const panelHeaderStyle = {
  fontSize: "30px",
  fontWeight: "800",
  color: "#0f172a",
  marginBottom: "8px",
};

const mutedTextStyle = {
  fontSize: "16px",
  color: "#64748b",
  marginBottom: "20px",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontSize: "16px",
  fontWeight: "700",
  color: "#334155",
};

const inputStyle = {
  width: "100%",
  padding: "16px 18px",
  marginBottom: "16px",
  borderRadius: "16px",
  border: "1px solid #d1d5db",
  fontSize: "17px",
  boxSizing: "border-box",
  background: "#ffffff",
};

const fileInputStyle = {
  width: "100%",
  padding: "16px 12px",
  borderRadius: "16px",
  border: "1px solid #d1d5db",
  fontSize: "16px",
  boxSizing: "border-box",
  background: "#ffffff",
};

const selectedFileStyle = {
  marginTop: "14px",
  marginBottom: "18px",
  fontSize: "15px",
  color: "#64748b",
};

const primaryButtonStyle = {
  width: "100%",
  padding: "16px",
  borderRadius: "18px",
  border: "none",
  background: "#0f172a",
  color: "white",
  fontSize: "18px",
  fontWeight: "700",
  cursor: "pointer",
  marginTop: "4px",
};

const topButtonStyle = {
  padding: "16px 24px",
  borderRadius: "16px",
  border: "none",
  background: "#0f172a",
  color: "white",
  fontSize: "17px",
  fontWeight: "600",
  cursor: "pointer",
  boxShadow: "0 8px 20px rgba(15,23,42,0.15)",
};

const savedHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "16px",
  flexWrap: "wrap",
  marginBottom: "18px",
};

const statsGridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: "18px",
  marginBottom: "20px",
};

const documentCardStyle = {
  background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  border: "1px solid #e2e8f0",
  borderRadius: "22px",
  padding: "22px",
};

const cardTopStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "start",
  gap: "16px",
  flexWrap: "wrap",
};

const documentTitleStyle = {
  fontSize: "24px",
  fontWeight: "800",
  color: "#0f172a",
  marginBottom: "8px",
};

const actionRowStyle = {
  display: "flex",
  gap: "10px",
  flexWrap: "wrap",
};

const openButtonStyle = {
  display: "inline-block",
  padding: "12px 18px",
  borderRadius: "14px",
  background: "#D4AF37",
  color: "#111111",
  textDecoration: "none",
  fontWeight: "700",
  cursor: "pointer",
};

const deleteButtonStyle = {
  padding: "12px 18px",
  borderRadius: "14px",
  background: "#dc2626",
  color: "white",
  border: "none",
  fontWeight: "700",
  cursor: "pointer",
};

const infoLineStyle = {
  fontSize: "15px",
  color: "#64748b",
  marginBottom: "6px",
};

const emptyStateStyle = {
  padding: "28px 0",
  color: "#94a3b8",
  fontSize: "17px",
};

const miniStatStyle = {
  background: "#f8fbff",
  borderRadius: "20px",
  padding: "20px",
  border: "1px solid #dbeafe",
};

const miniStatTitleStyle = {
  fontSize: "16px",
  fontWeight: "700",
  color: "#64748b",
  marginBottom: "10px",
};

const miniStatValueStyle = {
  fontSize: "42px",
  fontWeight: "800",
  color: "#0f172a",
  lineHeight: 1,
  marginBottom: "8px",
};

const miniStatSubtitleStyle = {
  fontSize: "14px",
  color: "#94a3b8",
};
